import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import './LibrarySelectModal.css';

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function LibrarySelectModal({ isOpen, mode, onConfirm, onClose }) {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const { fetchDocuments } = useApi();

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    setFetchError(null);
    setLoading(true);
    fetchDocuments()
      .then((all) => setDocs(
        all
          .filter((d) => d.status === 'processed')
          .map((d) => ({ ...d, id: d.id || d.SK?.replace('DOC#', '') || d.SK, name: d.name || d.filename, policyId: d.policyId || d.policy_id }))
      ))
      .catch((err) => setFetchError(err.message || 'Failed to load documents'))
      .finally(() => setLoading(false));
  }, [isOpen, fetchDocuments]);

  if (!isOpen) return null;

  const toggleDoc = (doc) => {
    setSelected((prev) => {
      const next = new Set(mode === 'single' ? [] : prev);
      if (prev.has(doc.id)) {
        next.delete(doc.id);
      } else {
        next.add(doc.id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(docs.filter((d) => selected.has(d.id)));
    onClose();
  };

  const canConfirm = selected.size > 0;

  const modal = (
    <div className="lsm-overlay" onClick={onClose}>
      <div className="lsm-container" onClick={(e) => e.stopPropagation()}>
        <div className="lsm-header">
          <h2>Select from library</h2>
          <button className="lsm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="lsm-list">
          {loading && <div className="lsm-empty">Loading...</div>}
          {!loading && fetchError && (
            <div className="lsm-empty lsm-error">Error: {fetchError}</div>
          )}
          {!loading && !fetchError && docs.length === 0 && (
            <div className="lsm-empty">No ready documents available.</div>
          )}
          {!loading && docs.map((doc) => (
            <label
              key={doc.id}
              className={`lsm-doc-row${selected.has(doc.id) ? ' selected' : ''}`}
            >
              <input
                type={mode === 'single' ? 'radio' : 'checkbox'}
                name="lsm-doc"
                checked={selected.has(doc.id)}
                onChange={() => toggleDoc(doc)}
                aria-label={`Select ${doc.name}`}
              />
              <span className="lsm-doc-name">{doc.name}</span>
              <span className="lsm-doc-meta">{formatFileSize(doc.fileSize)}</span>
            </label>
          ))}
        </div>

        <div className="lsm-footer">
          <button className="lsm-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="lsm-btn-confirm" onClick={handleConfirm} disabled={!canConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
